import { useCallback } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';

/**
 * Replaces two separate, both silently broken, "go back" mechanisms
 * (improvements/08-routing-tanstack-router.md, Section 08's "NavigationHistory
 * folded into router history" item):
 *
 * 1. `app.hooks.ts`'s `useBackUrl()` -- used by ten `BackButton`s across the
 *    app (subscriptions, games, section, date-token, all three preview
 *    implementations, issues, drafts, the single/double replica readers).
 *    Its body that would resolve `location.state.from` into an actual URL was
 *    commented out, so it always returned its `overWriteUrl` default (`'/'`)
 *    -- every "back" button in the reader silently went to home instead of
 *    the previous page, regardless of how the reader actually got there.
 * 2. `shared/lib/navigation-history.ts`'s module-level history stack, read by
 *    `reader/preview/preview.tsx` via `getPreviousNavigation()`.
 *    The only thing that ever populated it, `useNavigationHistory()`, had
 *    zero consumers anywhere (confirmed by grep, flagged as dead in that
 *    file's own doc comment during an earlier session) -- so the read was
 *    always `null`, same broken result via a different mechanism.
 *
 * Both existed because the browser History API has no "give me the previous
 * URL as a string" primitive -- only imperative `back()`/`forward()`/`go()`.
 * That's also why this hook returns a *callback*, not a URL: `<Link
 * to={backUrl}>` was never going to work reliably, which is exactly how both
 * predecessors ended up broken. `router.history.back()` is the platform-
 * correct way to do this, and it's already the pattern this codebase uses
 * elsewhere (`pages/issue/ui/issue.tsx`, `pages/article/model/
 * article.hooks.ts`): go back through real history when there is any,
 * otherwise land on a known-good fallback instead of a dead end.
 */
export function useGoBack(fallback = '/'): () => void {
	const router = useRouter();
	const navigate = useNavigate();

	return useCallback(() => {
		if (router.history.canGoBack()) {
			router.history.back();
			return;
		}
		navigate({ to: fallback, replace: true });
	}, [router, navigate, fallback]);
}
